import { useState, useEffect, useRef, useCallback } from 'react';
import { Search, X, Building2, ClipboardList, FolderOpen, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useDebounce } from '../../hooks/useDebounce';
import api from '../../api/client';
import type { GlobalSearchResult } from '../../types';

const categoryIcons: Record<string, React.ElementType> = {
  proje: Building2,
  gorev: ClipboardList,
  dosya: FolderOpen,
  kullanici: Users,
};

const categoryLabels: Record<string, string> = {
  proje: 'Projeler',
  gorev: 'Görevler',
  dosya: 'Dosyalar',
  kullanici: 'Kullanıcılar',
};

export function GlobalSearch() {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<GlobalSearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const debouncedQuery = useDebounce(query, 300);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsOpen((prev) => !prev);
      }
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!debouncedQuery.trim()) {
      setResults([]);
      setError(null);
      return;
    }
    const fetchResults = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const res = await api.get('/api/v1/search', { params: { q: debouncedQuery } });
        setResults(res.data.data || []);
      } catch (err: unknown) {
        const e = err as { response?: { data?: { message?: string } }; message?: string };
        setError(e.response?.data?.message || e.message || 'Arama yapılırken bir hata oluştu.');
        setResults([]);
      } finally {
        setIsLoading(false);
      }
    };
    fetchResults();
  }, [debouncedQuery]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [results]);

  const handleSelect = useCallback(
    (item: GlobalSearchResult) => {
      setIsOpen(false);
      navigate(item.url);
    },
    [navigate]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (results.length === 0) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((prev) => (prev + 1) % results.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((prev) => (prev - 1 + results.length) % results.length);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const item = results[selectedIndex];
        if (item) {
          handleSelect(item);
        }
      }
    },
    [results, selectedIndex, handleSelect]
  );

  const handleOverlayClick = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      setIsOpen(false);
    }
  }, []);

  const grouped = results.reduce<Record<string, GlobalSearchResult[]>>((acc, item) => {
    if (!acc[item.type]) acc[item.type] = [];
    acc[item.type].push(item);
    return acc;
  }, {});

  if (!isOpen) {
    return (
      <button
        type="button"
        data-testid="global-search-button"
        aria-label="Genel arama aç (⌘K)"
        onClick={() => setIsOpen(true)}
        className="flex h-11 min-w-11 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-500 shadow-sm hover:bg-slate-50"
      >
        <Search className="h-4 w-4" aria-hidden="true" />
        <span className="hidden sm:inline">Ara...</span>
        <kbd className="hidden rounded bg-slate-100 px-1.5 py-0.5 text-xs font-medium text-slate-500 md:inline">
          ⌘K
        </kbd>
      </button>
    );
  }

  return (
    <div
      ref={modalRef}
      data-testid="search-modal"
      role="dialog"
      aria-modal="true"
      aria-label="Genel arama"
      onClick={handleOverlayClick}
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 pt-20 sm:pt-[15vh]"
    >
      <div className="w-[calc(100%-2rem)] rounded-xl bg-white shadow-2xl sm:max-w-xl">
        <div className="flex items-center gap-3 border-b border-slate-100 px-4 py-3">
          <Search className="h-5 w-5 text-slate-400" aria-hidden="true" />
          <input
            ref={inputRef}
            data-testid="global-search-input"
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ara..."
            aria-label="Arama sorgusu"
            aria-autocomplete="list"
            aria-controls="search-results-list"
            aria-activedescendant={results.length > 0 ? `search-result-${results[selectedIndex]?.id}` : undefined}
            className="flex-1 bg-transparent text-base text-slate-900 outline-none placeholder:text-slate-400"
          />
          {isLoading && <div className="h-4 w-4 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" aria-label="Yükleniyor" />}
          <button
            type="button"
            aria-label="Aramayı kapat"
            onClick={() => setIsOpen(false)}
            className="rounded p-1 hover:bg-slate-100"
          >
            <X className="h-4 w-4 text-slate-500" aria-hidden="true" />
          </button>
        </div>
        <div
          id="search-results-list"
          data-testid="search-results"
          role="listbox"
          aria-label="Arama sonuçları"
          className="max-h-[50vh] overflow-y-auto p-2"
        >
          {error && (
            <div data-testid="search-error" className="px-4 py-6 text-center text-sm text-red-600" role="alert">
              {error}
            </div>
          )}
          {!error && results.length === 0 && debouncedQuery.trim() && !isLoading && (
            <div data-testid="search-no-results" className="px-4 py-8 text-center text-sm text-slate-500">
              Sonuç bulunamadı
            </div>
          )}
          {!error && results.length === 0 && !debouncedQuery.trim() && !isLoading && (
            <div data-testid="search-empty-state" className="px-4 py-8 text-center text-sm text-slate-500">
              Aramak için yazmaya başlayın
            </div>
          )}
          {Object.entries(grouped).map(([type, items]) => {
            const Icon = categoryIcons[type] || Search;
            return (
              <div key={type} data-testid={`search-category-${type}`} className="mb-2">
                <div className="flex items-center gap-2 px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-slate-400">
                  <Icon className="h-3.5 w-3.5" aria-hidden="true" />
                  {categoryLabels[type] || type}
                </div>
                {items.map((item) => {
                  const globalIndex = results.indexOf(item);
                  const isSelected = globalIndex === selectedIndex;
                  return (
                    <button
                      key={item.id}
                      id={`search-result-${item.id}`}
                      data-testid={`search-result-${item.type === 'dosya' ? 'file' : item.type}-${item.id}`}
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      onClick={() => handleSelect(item)}
                      onMouseEnter={() => setSelectedIndex(globalIndex)}
                      className={`flex w-full flex-col rounded-lg px-3 py-2 text-left text-sm ${
                        isSelected ? 'bg-blue-50 text-blue-700' : 'text-slate-700 hover:bg-slate-50'
                      }`}
                    >
                      <span data-testid={`search-result-title-${item.id}`} className="font-medium">
                        {item.title}
                      </span>
                      <span data-testid={`search-result-subtitle-${item.id}`} className="text-xs text-slate-400">
                        {item.subtitle}
                      </span>
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
        <div className="flex items-center justify-between border-t border-slate-100 px-4 py-2 text-xs text-slate-400">
          <div className="flex gap-3">
            <span>↑↓ Gezin</span>
            <span>↵ Seç</span>
            <span>Esc Kapat</span>
          </div>
          {results.length > 0 && <span>{results.length} sonuç</span>}
        </div>
      </div>
    </div>
  );
}
